/**
 * ChatFiltersBar — Barra filtri avanzati (stato, label, mie chat, non letti)
 */

import { X, User, Bell, BellRing, FolderKanban, Users, Info, Loader2 } from 'lucide-react';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { LabelBadge } from '../shared/LabelBadge';
import { tip } from '@/components/messages/ChatTooltip';
import { DA_GESTIRE_GIORNI } from '@/lib/services/whatsapp/chatListRules';

const STATUS_OPTIONS = [
  { value: 'da_gestire', label: 'Da gestire', color: '#F97316', hint: `Chat da gestire con messaggi negli ultimi ${DA_GESTIRE_GIORNI} giorni` },
  { value: 'in_gestione', label: 'In gestione', color: '#3B82F6', hint: 'Chat che qualcuno sta seguendo' },
  { value: 'chiusa', label: 'Chiusa', color: '#6B7280', hint: 'Chat chiuse (si riaprono da sole se il cliente riscrive)' },
] as const;

interface Props {
  hasActiveFilters: boolean;
  /** true se ci sono altre chat non ancora caricate: i filtri lavorano solo su quelle caricate. */
  partialData?: boolean;
  loadedCount?: number;
  onLoadAll?: () => void;
  loadingAll?: boolean;
}

export function ChatFiltersBar({ hasActiveFilters, partialData = false, loadedCount = 0, onLoadAll, loadingAll = false }: Props) {
  const { filters, setFilters, resetFilters, orgLabels } = useWhatsAppChatMetaStore();

  const activeLabel = orgLabels.find((l) => l.id === filters.labelId);

  return (
    <div className="px-3 pb-2 space-y-2">
      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {STATUS_OPTIONS.map(({ value, label, color, hint }) => (
          <button
            key={value}
            {...tip(hint)}
            onClick={() => setFilters({ status: filters.status === value ? null : value })}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
              filters.status === value
                ? 'border-transparent text-white'
                : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-current'
            }`}
            style={filters.status === value ? { backgroundColor: color, borderColor: color } : { color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {label}
          </button>
        ))}
      </div>

      {/* Second row: label + my chats + unread */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {/* Label filter */}
        {orgLabels.length > 0 && (
          <div className="relative group">
            {activeLabel ? (
              <span className="flex items-center gap-1">
                <LabelBadge name={activeLabel.name} color={activeLabel.color} />
                <button onClick={() => setFilters({ labelId: null })} aria-label="Togli il filtro etichetta" {...tip('Togli il filtro etichetta')} className="text-[#787774] hover:text-red-500 text-xs leading-none">×</button>
              </span>
            ) : (
              <div className="relative">
                <select
                  value={filters.labelId || ''}
                  onChange={(e) => setFilters({ labelId: e.target.value || null })}
                  className="text-[11px] text-[#787774] border border-[rgba(55,53,47,0.15)] dark:border-white/15 rounded-full px-2 py-0.5 bg-transparent cursor-pointer outline-none hover:border-[rgba(55,53,47,0.3)] appearance-none pr-4"
                >
                  <option value="">+ Label</option>
                  {orgLabels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {/* My chats toggle */}
        <button
          onClick={() => setFilters({ myChatsOnly: !filters.myChatsOnly })}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            filters.myChatsOnly
              ? 'bg-[#2383E2] border-[#2383E2] text-white'
              : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-[#2383E2] hover:text-[#2383E2]'
          }`}
        >
          <User className="w-3 h-3" />
          Le mie
        </button>

        {/* My projects toggle */}
        <button
          onClick={() => setFilters({ myProjectsOnly: !filters.myProjectsOnly })}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            filters.myProjectsOnly
              ? 'bg-[#9065B0] border-[#9065B0] text-white'
              : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-[#9065B0] hover:text-[#9065B0]'
          }`}
        >
          <FolderKanban className="w-3 h-3" />
          I miei progetti
        </button>

        {/* Unread toggle */}
        <button
          onClick={() => setFilters({ unreadOnly: !filters.unreadOnly })}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            filters.unreadOnly
              ? 'bg-[#2383E2] border-[#2383E2] text-white'
              : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-[#2383E2] hover:text-[#2383E2]'
          }`}
        >
          <Bell className="w-3 h-3" />
          Non letti
        </button>

        {/* Groups toggle */}
        <button
          onClick={() => setFilters({ groupsOnly: !filters.groupsOnly })}
          {...tip('Solo le chat di gruppo')}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            filters.groupsOnly
              ? 'bg-[#0F7B6C] border-[#0F7B6C] text-white'
              : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-[#0F7B6C] hover:text-[#0F7B6C]'
          }`}
        >
          <Users className="w-3 h-3" />
          Gruppi
        </button>

        {/* Promemoria "Ricontatta" scaduti */}
        <button
          onClick={() => setFilters({ recontactOnly: !filters.recontactOnly })}
          {...tip('Chat col promemoria di ricontatto arrivato a scadenza')}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
            filters.recontactOnly
              ? 'bg-[#D9730D] border-[#D9730D] text-white'
              : 'border-[rgba(55,53,47,0.15)] dark:border-white/15 text-[#787774] hover:border-[#D9730D] hover:text-[#D9730D]'
          }`}
        >
          <BellRing className="w-3 h-3" />
          Da ricontattare
        </button>

        {/* Reset */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-red-500 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <X className="w-3 h-3" />
            Reset
          </button>
        )}
      </div>

      {/* I filtri vedono solo le chat gia' caricate: lo si dice e si offre di caricarle tutte */}
      {partialData && hasActiveFilters && (
        <div className="flex items-start gap-2 rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 px-2.5 py-2 text-[11px] text-[#787774]">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span className="flex-1">
            I filtri guardano solo le {loadedCount} chat caricate: ce ne sono altre piu' vecchie.
            {onLoadAll && (
              <button
                type="button"
                onClick={onLoadAll}
                disabled={loadingAll}
                className="ml-1 inline-flex items-center gap-1 font-medium text-[#2383E2] hover:underline disabled:opacity-60"
              >
                {loadingAll && <Loader2 className="w-3 h-3 animate-spin" />}
                {loadingAll ? 'Carico...' : 'Carica tutte'}
              </button>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
